import {ServiceBase} from "../ServiceBase";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {FeaturesMaxDisplayCountMap} from "./FeaturesMaxDisplayCountMap";
import {
    FeatureToggleManagementData,
    IFeaturesToggleManagementService
} from "./features-toggle-management.service.interface";
import {FeatureId} from "./FeatureId";
import {Check} from "@sparkware/app-utils";
import {FeatureToggleManagementController_SetFeatureCounterRequest} from "../api/api.service";

export class FeaturesToggleManagementService extends ServiceBase implements IFeaturesToggleManagementService {
    private _featuresDisplayCount : Partial<Record<FeatureId, number>> = {};
    private _serverCallPromise = Promise.resolve();
    constructor(services: IServiceFactoryExtended) {
        super(services);

        this.reaction(() => this.services.user.isAuthorized,
            () => {
                if(this.services.user.isAuthorized) {
                    this._serverCallPromise = this._readFeatureToggleDataFromServer();
                } else {
                    this._resetData();
                }
            }, {
                fireImmediately: true
            });
    }

    public async shouldShowFeature(featureID: number): Promise<boolean> {
        if(!this.services.user.isAuthorized) {
            // this service not support anonymous users. it's only checks if for specific cid it not shown max times
            return false;
        }

        // We still not got answer from the server
        if(Check.isEmpty(this._featuresDisplayCount)) {
            if(!this._serverCallPromise) {
                this._serverCallPromise = this._readFeatureToggleDataFromServer();
            }

            try{
                await this._serverCallPromise;
            } catch(err){
                this.services.logger.warning('Fail to read FeatureToggleDataFromServer. err:' + err);
                return false;
            }
        }

        return (this._featuresDisplayCount[featureID] ?? 0) < this._getMaxDisplays(featureID)
    }

    public async updateFeatureCounter(featureID: number): Promise<void> {
        let displayCount = (this._featuresDisplayCount[featureID] ?? 0) + 1;

        const request: FeatureToggleManagementController_SetFeatureCounterRequest = {
            FeatureID: featureID
        };

        this._featuresDisplayCount[featureID] = displayCount;
        await this.services.api.featureToggleManagementController.setFeatureCounter(request);
    }

    private _getMaxDisplays(featureID: FeatureId) {
        if(FeaturesMaxDisplayCountMap[featureID]) {
            return FeaturesMaxDisplayCountMap[featureID];
        }

        // Default is 1.
        return 1;
    }

    private async _readFeatureToggleDataFromServer(): Promise<void> {
        try {
            const featuresData = await this.services.api.featureToggleManagementController.getFeaturesDataForCID();
            if (featuresData) {
                this._convertFeatureToggleManagementDataArrayToRecord(featuresData);
            }
        } catch(err){
            this.services.logger.error("Fail to read feature toggle data from token.", err);
        }
    }

    private _convertFeatureToggleManagementDataArrayToRecord(featuresToggleData: FeatureToggleManagementData[]) {
        this._featuresDisplayCount = featuresToggleData.reduce((record, obj) => {
            record[obj.FeatureID] = obj.DisplayCount;
            return record;
        }, {});
    }

    private _resetData() {
        this._serverCallPromise = Promise.resolve();
        this._featuresDisplayCount = {};
    }
}
